import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { s4aDone } from './section4Steps';
import { previewBlob, saveBlob } from './downloadFile';
import { GOLD, NAVY_FORM } from './colors';
import { LoadError, useIsNarrow } from './ui';
import { useDarkMode } from './DarkModeContext';
import {
  CERTIFIED_FUNCTIONS,
  NON_CERTIFIED_FUNCTION_GROUPS,
  nonCertifiedValue,
} from './NewStarterForm';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

// COMPCARD: the COMPLIANCE role's employee card.
//
// SUPERSEDES the earlier simplified card (CLASSCARD). That version showed a short summary; this
// one is built to RESEMBLE THE COO CARD, minus a named restriction list, which is a different
// shape of screen and so a rewrite of this file rather than an addition to it.
//
// WHY A SEPARATE COMPONENT RATHER THAN A COO VARIANT. EmployeeProfile.js is ~3,000 lines wired to
// roughly forty endpoints, most of them PATCH writes (name, job title, company email, manager,
// visa, delegate email, personal email, and more). Reusing it behind a role flag would mean every
// one of those writes becomes a conditional that has to be audited now and re-audited on every
// future edit, and a single missed branch silently hands the compliance role a write it must not
// have. A separate component cannot inherit a write path it does not contain, so the blast radius
// of a future mistake here is bounded by this file. The COO card is not touched by this work.
//
// THE RESTRICTED FIELDS ARE NOT MERELY UNRENDERED HERE. NI number, phone, personal email,
// signature and photo are absent from the SERVER RESPONSE: the compliance role receives
// EmployeeComplianceResponse, which does not declare those fields at all (see schemas.py). This
// component could not display them if it tried. Hiding them in JSX alone was explicitly not
// acceptable, and would not have survived anyone opening the network tab.
//
// WHAT IT FETCHES, all pre-existing endpoints:
//   GET /employees/{id}                     identity and profile fields (narrowed server side)
//   GET /new-starter/{id}                   Section 4 state and the stored classification
//   GET /documents/employee/{id}            the background check (compliance receives ONLY the
//                                           document types its grant covers, filtered server side)
//   GET /compliance/employee-detail/{id}    declarations, attestations, pending compliance tasks
//
// THE ONLY WRITES are the narrow classification endpoint and the background-check ARCHIVE, both
// enforced server side. BGVIEWONLY removed the background-check UPLOAD from this card: the
// background check is view only here and is uploaded through Fill Compliance (Section 4). There is
// no other mutating call in this file, asserted by a source guard on executable lines.

const splitCsv = (v) => (v || '').split(',').map(s => s.trim()).filter(Boolean);

// CLASSHISTVIEW: a classification rendered READABLY, for one history entry.
//
// `prev` reads the entry's previous side instead of its current one, so one function serves both the
// "Classification" and the "Previously" column and the two can never be formatted differently.
//
// THE FOUR FIELDS ARE NOT INTERCHANGEABLE, so this does not just join them. "None of these apply" is
// an explicit POSITIVE statement that the employee carries no classification, which is a different
// fact from having no record at all - that distinction is the whole reason
// classification_none_confirmed exists as its own column, and flattening it to an empty string here
// would throw it away on the one screen built to show it.
const classificationSummary = (entry, prev = false) => {
  if (!entry) return '-';
  const g = (k) => entry[prev ? `prev_${k}` : k];
  const cert = splitCsv(g('certified_functions'));
  const nonCert = splitCsv(g('non_certified_functions'));
  const parts = [];
  if (cert.length) parts.push(`Certified: ${cert.join(', ')}`);
  if (nonCert.length) parts.push(`Non-certified: ${nonCert.join(', ')}`);
  if (g('senior_management')) parts.push('Senior Management');
  if (parts.length) return parts.join(' / ');
  if (g('classification_none_confirmed')) return 'None of these apply (confirmed)';
  return '-';
};

const fmtApiError = (e) => {
  const detail = e.response?.data?.detail;
  if (!detail) return e.message || 'Could not save.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      const loc = d.loc ? d.loc.filter(x => x !== 'body').join('.') : '';
      return loc ? `${loc}: ${d.msg}` : d.msg;
    }).join(' | ');
  }
  return 'Could not save.';
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = toDisplayDate(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// The four declaration registers plus their columns, in the confirmed order. Table-driven so row
// chrome is defined once rather than in four near-identical blocks.
const DECLARATION_SECTIONS = [
  { key: 'outside_affiliations', title: 'Outside Affiliations',
    cols: [['affiliated_entity', 'Entity'], ['position', 'Position'],
           ['date_commenced', 'Commenced', fmtDate], ['date_ceased', 'Ceased', fmtDate]] },
  { key: 'gifts', title: 'Gifts and Entertainment',
    cols: [['entry_type', 'Type'], ['nature', 'Nature'], ['firm_name', 'Firm'],
           ['amount', 'Amount'], ['date', 'Date', fmtDate]] },
  { key: 'pad', title: 'Personal Account Dealing',
    cols: [['security', 'Security'], ['direction', 'Direction'],
           ['trade_type', 'Trade type'], ['trade_date', 'Trade date', fmtDate]] },
  { key: 'public_officials', title: 'Public Officials',
    cols: [['official_names', 'Official'], ['means_of_engagement', 'Engagement'],
           ['engagement_date', 'Date', fmtDate]] },
];

// The profile rows this card shows. Mirrors the COO card's identity block MINUS the restricted
// fields, which are not in the response at all. Every one of these renders as TEXT: there is no
// control bound to any of them, and no endpoint in this file can write them.
const PROFILE_ROWS = [
  ['Company email', 'company_email'],
  ['Job title', 'job_title'],
  ['Department', 'department'],
  ['Team', 'function_team'],
  ['Entity', 'primary_entity'],
  ['Secondary entity', 'secondary_entity'],
  ['Location', 'location'],
  ['Employee type', 'employee_type'],
  ['Start date', 'start_date', fmtDate],
  ['Nationality', 'nationality'],
  ['Country of residence', 'country_of_residence'],
  ['Requires visa', 'requires_visa', v => (v ? 'Yes' : 'No')],
  ['Residence permit', 'has_residence_permit', v => (v ? 'Yes' : 'No')],
  ['Gender', 'gender'],
];

// COMPCARDLAYOUT: this component now renders INSIDE the compliance shell's content area, so it
// owns neither the page chrome nor the way back.
//   - `employeeId` comes from the ROUTE (/compliance/employees/:employeeId), so the card survives
//     a refresh and can be opened by URL. `employee` is the list row when the shell already has
//     it, used only for an instant first paint before the profile fetch resolves; it may be null
//     on a direct URL open, which is why every render path below falls back to the fetched
//     profile rather than assuming the prop.
//   - `onBack` was REMOVED along with the standalone "Back to Employees" button. Going back is the
//     breadcrumb's job now, exactly as on the COO side, and it navigates rather than clearing
//     state so the browser Back button agrees with it.
export default function ComplianceEmployeeCard({ employeeId, employee, viewerEmployeeId, onFillCompliance }) {
  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const isNarrow = useIsNarrow();

  const [profile, setProfile] = useState(null);
  const [detail, setDetail] = useState(null);
  const [nsForm, setNsForm] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [editing, setEditing] = useState(false);
  const [certSel, setCertSel] = useState([]);
  const [nonCertSel, setNonCertSel] = useState([]);
  const [seniorMgmt, setSeniorMgmt] = useState(false);
  const [noneConfirmed, setNoneConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedNote, setSavedNote] = useState(false);

  // CLASSHISTVIEW: the classification history and the optional as-at answer. READ ONLY - this panel
  // has no edit and no delete control anywhere, because the history is append-only: a correction is
  // made by making a NEW classification change, which writes its own row.
  const [classHist, setClassHist] = useState(null);
  const [classHistError, setClassHistError] = useState(false);
  const [asAt, setAsAt] = useState('');
  const [asAtResult, setAsAtResult] = useState(null);
  const [asAtBusy, setAsAtBusy] = useState(false);
  const [bgBusy, setBgBusy] = useState(false);
  const [bgError, setBgError] = useState(null);

  // The route id wins; the list row is only a paint-ahead convenience and is absent on a
  // direct URL open.
  const empId = employeeId || employee?.id || null;

  const load = useCallback(() => {
    if (!empId) return;
    setLoading(true);
    setLoadFailed(false);
    // allSettled, not all: a missing Section 4 form or an empty document list is legitimate, and
    // one of them failing must degrade its own panel rather than blanking the whole card.
    Promise.allSettled([
      axios.get(`${API}/employees/${empId}`),
      axios.get(`${API}/compliance/employee-detail/${empId}`),
      axios.get(`${API}/new-starter/${empId}`),
      axios.get(`${API}/documents/employee/${empId}`),
      // CLASSHISTVIEW: its own request, inside the same allSettled as the others, so a history that
      // fails to load degrades its OWN panel rather than blanking the whole card.
      axios.get(`${API}/compliance/classification-history/${empId}`),
    ]).then(([pRes, dRes, nRes, docRes, chRes]) => {
      setProfile(pRes.status === 'fulfilled' ? (pRes.value.data || null) : null);
      if (dRes.status === 'fulfilled') {
        setDetail(dRes.value.data);
      } else {
        setDetail(null);
        setLoadFailed(true);
      }
      setNsForm(nRes.status === 'fulfilled' ? (nRes.value.data || null) : null);
      setDocs(docRes.status === 'fulfilled' ? (docRes.value.data?.documents || []) : []);
      // CLASSHISTVIEW: a failed history load sets its own error flag, so the panel says it could not
      // load rather than rendering an empty list - which would read as "this employee has no history",
      // a claim the card cannot support when the request did not come back.
      if (chRes.status === 'fulfilled') {
        setClassHist(chRes.value.data || null);
        setClassHistError(false);
      } else {
        setClassHist(null);
        setClassHistError(true);
      }
      setLoading(false);
    });
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setCertSel(splitCsv(nsForm?.certified_functions));
    setNonCertSel(splitCsv(nsForm?.non_certified_functions));
    setSeniorMgmt(!!nsForm?.senior_management);
    setNoneConfirmed(!!nsForm?.classification_none_confirmed);
  }, [nsForm]);

  const T = {
    card:    { backgroundColor: isDarkMode ? '#16233d' : '#fff', borderRadius: '10px', padding: '20px',
               marginBottom: '16px', border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#EEE'}`,
               boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)' },
    title:   { fontSize: '14px', fontWeight: 600, color: isDarkMode ? '#e8eaf0' : NAVY_FORM, margin: '0 0 14px 0' },
    label:   { fontSize: '11px', color: isDarkMode ? '#8b95a8' : '#aaa', textTransform: 'uppercase',
               letterSpacing: '0.05em', fontWeight: 600, margin: '0 0 4px 0' },
    value:   { fontSize: '14px', color: isDarkMode ? '#e8eaf0' : '#333', margin: 0 },
    muted:   { color: isDarkMode ? '#8b95a8' : '#667085', fontStyle: 'italic', fontSize: '13px', margin: 0 },
    btn:     { backgroundColor: NAVY_FORM, color: '#fff', border: 'none', borderRadius: '6px',
               padding: '7px 16px', fontSize: '12px', fontWeight: 500, cursor: 'pointer' },
    btnGhost:{ background: 'none', color: isDarkMode ? '#e8eaf0' : NAVY_FORM,
               border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.3)' : '#ddd'}`, borderRadius: '6px',
               padding: '7px 16px', fontSize: '12px', cursor: 'pointer' },
    th:      { textAlign: 'left', fontSize: '11px', color: isDarkMode ? '#8b95a8' : '#999',
               textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600,
               padding: '6px 10px', borderBottom: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#eee'}` },
    td:      { fontSize: '13px', color: isDarkMode ? '#e8eaf0' : '#333', padding: '8px 10px',
               borderBottom: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.08)' : '#f5f5f5'}` },
    pill:    (bg, fg) => ({ backgroundColor: bg, color: fg, borderRadius: '10px', padding: '2px 8px',
               fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }),
  };

  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  const hasAny = certSel.length > 0 || nonCertSel.length > 0 || seniorMgmt;

  const saveClassification = () => {
    setSaving(true);
    setSaveError(null);
    // The narrow endpoint. Its body model forbids extra keys, so this shape is not a convention
    // here but the only shape the server will accept.
    axios.patch(`${API}/new-starter/${empId}/classification`, {
      certified_functions: certSel.join(', '),
      non_certified_functions: nonCertSel.join(', '),
      senior_management: seniorMgmt,
      classification_none_confirmed: noneConfirmed,
    })
      .then(res => {
        setNsForm(res.data);
        setEditing(false);
        setSavedNote(true);
        setTimeout(() => setSavedNote(false), 3000);
      })
      .catch(e => setSaveError(fmtApiError(e)))
      .finally(() => setSaving(false));
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveError(null);
    setCertSel(splitCsv(nsForm?.certified_functions));
    setNonCertSel(splitCsv(nsForm?.non_certified_functions));
    setSeniorMgmt(!!nsForm?.senior_management);
    setNoneConfirmed(!!nsForm?.classification_none_confirmed);
  };

  // BACKGROUND CHECK. Upload, view and archive, the same three actions the COO side offers,
  // through the same generic document endpoints. The compliance role is admitted to them server
  // side for this document type only and within its entity scope, so the controls below cannot
  // reach any other document even if this component asked.
  // COMPSOD (segregation of duties): a compliance officer must not upload or archive the
  // background check on their OWN record - on that one document they would be both the subject
  // of the evidence and the officer attesting to it. Viewing their own stays allowed, so only the
  // two MUTATING controls are withheld and the panel itself still renders.
  //
  // THIS IS NOT THE CONTROL. The server refuses both actions for this case
  // (compliance_may_act_on_document's `mutating` flag, verify_compsod.py N-1/N-2), so hiding the
  // buttons only avoids offering something that would 403. A null viewerEmployeeId (the identity
  // not yet resolved) is treated as NOT self, because the server is the gate either way and
  // withholding controls on an unresolved identity would hide them from every legitimate record.
  const isOwnRecord = !!viewerEmployeeId && viewerEmployeeId === (empId || null);

  const bgDocs = docs.filter(d => d.type === 'background_check' && !d.is_archived);
  const bgArchived = docs.filter(d => d.type === 'background_check' && d.is_archived);

  // BGVIEWONLY: the uploadBg handler was REMOVED with the button it served. Uploading a
  // background check now happens only through Fill Compliance (Section 4). The server refuses
  // a compliance upload on /documents/upload too, so this is not the only control.

  // DLNAME: Preview and Download side by side, the same pair the PAD attachment panel offers.
  // View opens a tab and is byte-unchanged. Download saves under the SERVER'S name, which is the
  // half that was missing: with no `download` attribute the browser named the file after the blob
  // URL's last segment, a generated UUID that read as the document id.
  const viewBg = (docId) => {
    setBgError(null);
    axios.get(`${API}/documents/${docId}/view`, { responseType: 'blob' })
      .then(res => previewBlob(res))
      .catch(e => setBgError(fmtApiError(e)));
  };

  const downloadBg = (docId) => {
    setBgError(null);
    axios.get(`${API}/documents/${docId}/view`, { responseType: 'blob' })
      .then(res => saveBlob(res))
      .catch(e => setBgError(fmtApiError(e)));
  };

  const archiveBg = (docId) => {
    setBgBusy(true);
    setBgError(null);
    axios.post(`${API}/documents/${docId}/archive`)
      .then(() => load())
      .catch(e => setBgError(fmtApiError(e)))
      .finally(() => setBgBusy(false));
  };

  // Section 4 state. The same signal the Oversight list row uses, so the button here and the row
  // there can never disagree.
  // S4AB: compliance's part of Section 4 is 4A, Background check classification.
  const s4Done = s4aDone(nsForm);

  // TASKS: only the compliance tasks this employee still has to complete. The server already
  // scopes employee-detail's pending_tasks to the compliance-owned task types and to pending
  // status, so no other task type can appear here.
  const pendingTasks = detail?.pending_tasks || [];

  const atts = detail?.attestations || [];
  const attsOutstanding = atts.filter(a => a.status !== 'completed' && !a.attested_at);
  const attsCompleted   = atts.filter(a => a.status === 'completed' || a.attested_at);

  const storedCert = splitCsv(nsForm?.certified_functions);
  const storedNonCert = splitCsv(nsForm?.non_certified_functions);
  const storedSenior = !!nsForm?.senior_management;
  const storedNone = !!nsForm?.classification_none_confirmed;
  const hasStoredClassification = storedCert.length > 0 || storedNonCert.length > 0 || storedSenior;

  const p = profile || employee || {};

  return (
    <div>
      {/* IDENTITY + PROFILE. Every row is text; nothing here is editable. */}
      <div style={T.card}>
        {/* CARDHEAD: the same header pattern used by the COO card, so it gets the same treatment.
            This one never had the overlap bug (the pill carries no flexShrink: 0, so it squashed
            rather than riding over the name), but the row could not wrap and the name block could
            not grow, so a long title and entity still broke into a narrow column. flexWrap lets
            the pill drop below, and flex on the name block lets it use the width. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px',
                      flexWrap: 'wrap' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: NAVY_FORM,
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, fontWeight: 600, flexShrink: 0 }}>
            {(p.first_name || '?').charAt(0)}
          </div>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <p style={{ ...T.value, fontWeight: 600, fontSize: '18px' }}>
              {p.first_name} {p.last_name}
            </p>
            {/* COMPCARDLAYOUT: job title AND entity in the header block, as the COO card's
                header carries them, rather than the entity appearing only as a field row. */}
            <p style={{ ...T.muted, fontStyle: 'normal' }}>
              {p.job_title || '-'}
              {p.primary_entity ? ` · ${p.primary_entity}` : ''}
            </p>
          </div>
          {p.is_active === false && (
            <span style={{ ...T.pill('#fdf5f5', '#A63A3A'), marginLeft: 'auto' }}>Former employee</span>
          )}
        </div>

        <div style={{ display: 'grid',
                      gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3, minmax(0, 1fr))',
                      gap: '14px' }}>
          {PROFILE_ROWS.map(([label, field, fmt]) => (
            <div key={label}>
              <p style={T.label}>{label}</p>
              <p style={T.value}>
                {fmt ? fmt(p[field]) : (p[field] || '-')}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CLASSIFICATION - the ONE editable field on this card. */}
      <div style={T.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: '12px', marginBottom: '12px' }}>
          <h3 style={{ ...T.title, margin: 0 }}>Regulatory classification</h3>
          {!editing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {savedNote && <span style={T.pill('#eaf3ee', '#2D4A3E')}>Saved</span>}
              <button style={T.btn} onClick={() => setEditing(true)}>Edit classification</button>
            </div>
          )}
        </div>

        {!editing ? (
          <div>
            {!hasStoredClassification && !storedNone ? (
              <p style={T.muted}>No classification recorded yet.</p>
            ) : storedNone && !hasStoredClassification ? (
              <p style={T.value}>None of these apply (confirmed).</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {storedCert.length > 0 && (
                  <div>
                    <p style={T.label}>Certified Function</p>
                    <p style={T.value}>{storedCert.join(', ')}</p>
                  </div>
                )}
                {storedNonCert.length > 0 && (
                  <div>
                    <p style={T.label}>Non-Certified Function</p>
                    <p style={T.value}>{storedNonCert.join(', ')}</p>
                  </div>
                )}
                {storedSenior && (
                  <div>
                    <p style={T.label}>Senior Management</p>
                    <p style={T.value}>Yes</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            <p style={{ ...T.label, marginBottom: '8px' }}>Certified Function</p>
            <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'repeat(2, 1fr)',
                          gap: '6px', marginBottom: '16px' }}>
              {CERTIFIED_FUNCTIONS.map(f => (
                <label key={f} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                                        color: isDarkMode ? '#e8eaf0' : '#333', cursor: 'pointer' }}>
                  <input type="checkbox" checked={certSel.includes(f)}
                         onChange={() => toggle(certSel, setCertSel, f)} />
                  {f}
                </label>
              ))}
            </div>

            <p style={{ ...T.label, marginBottom: '8px' }}>Non-Certified Function</p>
            <div style={{ marginBottom: '16px' }}>
              {NON_CERTIFIED_FUNCTION_GROUPS.map(g => (
                <div key={g.jurisdiction} style={{ marginBottom: '10px' }}>
                  <p style={{ fontSize: '12px', fontWeight: 700,
                              color: isDarkMode ? '#c9a84c' : NAVY_FORM, margin: '0 0 4px 0' }}>
                    {g.jurisdiction}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'repeat(2, 1fr)',
                                gap: '6px' }}>
                    {g.roles.map(role => {
                      const v = nonCertifiedValue(g.jurisdiction, role);
                      return (
                        <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '8px',
                                                fontSize: '13px', color: isDarkMode ? '#e8eaf0' : '#333',
                                                cursor: 'pointer' }}>
                          <input type="checkbox" checked={nonCertSel.includes(v)}
                                 onChange={() => toggle(nonCertSel, setNonCertSel, v)} />
                          {role}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                            color: isDarkMode ? '#e8eaf0' : '#333', cursor: 'pointer', marginBottom: '10px' }}>
              <input type="checkbox" checked={seniorMgmt}
                     onChange={e => setSeniorMgmt(e.target.checked)} />
              Senior Management
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                            color: isDarkMode ? '#e8eaf0' : '#333',
                            cursor: hasAny ? 'not-allowed' : 'pointer', opacity: hasAny ? 0.5 : 1,
                            marginBottom: '14px' }}>
              <input type="checkbox" checked={noneConfirmed} disabled={hasAny}
                     onChange={e => setNoneConfirmed(e.target.checked)} />
              None of these apply
            </label>

            {saveError && (
              <p style={{ color: '#A63A3A', fontSize: '13px', margin: '0 0 10px 0' }}>{saveError}</p>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...T.btn, opacity: saving ? 0.6 : 1 }} disabled={saving}
                      onClick={saveClassification}>
                {saving ? 'Saving...' : 'Save classification'}
              </button>
              <button style={T.btnGhost} disabled={saving} onClick={cancelEdit}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* CLASSIFICATION HISTORY (CLASSHISTVIEW) - READ ONLY, directly under Regulatory classification.
          WHY IT EXISTS: CLASSHIST records every change, but nothing showed it, so answering "what was
          this person certified for in March" meant a database query.
          NO EDIT AND NO DELETE CONTROL, anywhere in this panel, and that is deliberate rather than an
          omission: the history is append-only and a correction is made by making a NEW classification
          change, which writes its own row. An edit control here would let the trail be rewritten.
          NAMES, NEVER AN EMAIL: changed_by_name is resolved server side by COMPBYNAME and is null when
          it cannot be resolved, so this renders a neutral label rather than an address. */}
      <div style={T.card}>
        <h3 style={T.title}>Classification history</h3>

        {classHistError && (
          <p style={{ color: '#A63A3A', fontSize: '13px', margin: '0 0 10px 0' }}>
            Could not load the classification history.
          </p>
        )}

        {!classHistError && (!classHist || (classHist.entries || []).length === 0) && (
          <p style={T.muted}>No recorded classification change for this employee.</p>
        )}

        {!classHistError && classHist && (classHist.entries || []).length > 0 && (
          <>
            {/* AS AT DATE: the classification in force at the END of the chosen day, derived from the
                history by the server rather than recomputed here, so the panel and the answer cannot
                disagree. A change made during that day IS in force at the end of it. */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', marginBottom: '14px',
                          flexWrap: 'wrap' }}>
              <div>
                <p style={T.label}>Classification as at</p>
                <input
                  type="date"
                  aria-label="Classification as at date"
                  value={asAt}
                  onChange={e => setAsAt(e.target.value)}
                  style={{ ...T.btnGhost, cursor: 'text', padding: '6px 10px' }}
                />
              </div>
              <button
                style={T.btn}
                disabled={!asAt || asAtBusy}
                onClick={() => {
                  setAsAtBusy(true);
                  axios.get(`${API}/compliance/classification-history/${empId}`,
                            { params: { as_at: asAt } })
                    .then(r => setAsAtResult(r.data || null))
                    .catch(() => setAsAtResult({ as_at_error: 'Could not load that date.' }))
                    .finally(() => setAsAtBusy(false));
                }}
              >
                {asAtBusy ? 'Checking...' : 'Show'}
              </button>
            </div>

            {asAtResult && (
              <div style={{ marginBottom: '14px', padding: '10px 12px', borderRadius: '6px',
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f7f7f5' }}>
                {asAtResult.as_at_error ? (
                  <p style={{ ...T.value, margin: 0 }}>{asAtResult.as_at_error}</p>
                ) : asAtResult.as_at_entry ? (
                  <>
                    <p style={T.label}>In force on {asAtResult.as_at}</p>
                    <p style={{ ...T.value, margin: 0 }}>
                      {classificationSummary(asAtResult.as_at_entry)}
                    </p>
                  </>
                ) : (
                  /* A real answer, not an error: the employee had no classification yet on that date. */
                  <p style={{ ...T.value, margin: 0 }}>
                    No classification was recorded for this employee on {asAtResult.as_at}.
                  </p>
                )}
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={T.th}>Date</th>
                    <th style={T.th}>Changed by</th>
                    <th style={T.th}>Source</th>
                    <th style={T.th}>Classification</th>
                    <th style={T.th}>Previously</th>
                  </tr>
                </thead>
                <tbody>
                  {classHist.entries.map(en => (
                    <tr key={en.id}>
                      <td style={{ ...T.td, whiteSpace: 'nowrap' }}>{fmtDate(en.changed_at)}</td>
                      <td style={T.td}>{en.changed_by_name || 'Unknown'}</td>
                      <td style={T.td}>
                        <span style={en.is_baseline
                          ? T.pill(isDarkMode ? 'rgba(255,255,255,0.08)' : '#f1f1ef',
                                   isDarkMode ? '#c7d2e4' : '#5c5c57')
                          : { fontSize: '13px' }}>
                          {en.source_label}
                        </span>
                      </td>
                      <td style={T.td}>{classificationSummary(en)}</td>
                      <td style={T.td}>
                        {/* A BASELINE row has no previous values, because none existed. Saying so is
                            honest; an empty cell would read as a deletion. */}
                        {en.is_baseline
                          ? <span style={T.muted}>Starting point</span>
                          : classificationSummary(en, true)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* BACKGROUND CHECK - upload, view, archive. */}
      <div style={T.card}>
        <h3 style={T.title}>Background check</h3>
        {bgError && <p style={{ color: '#A63A3A', fontSize: '13px', margin: '0 0 10px 0' }}>{bgError}</p>}

        {bgDocs.length === 0 ? (
          <p style={{ ...T.muted, marginBottom: '12px' }}>{t('compliance.bgNoneOnFile')}</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
            <thead><tr>
              <th style={T.th}>File</th><th style={T.th}>Uploaded</th><th style={T.th}>Actions</th>
            </tr></thead>
            <tbody>
              {bgDocs.map(d => (
                <tr key={d.id}>
                  <td style={T.td}>{d.file_name}</td>
                  <td style={T.td}>{fmtDate(d.uploaded_at)}</td>
                  <td style={T.td}>
                    <button style={{ ...T.btnGhost, marginRight: '8px' }}
                            onClick={() => viewBg(d.id)}>View</button>
                    <button style={{ ...T.btnGhost, marginRight: '8px' }}
                            onClick={() => downloadBg(d.id)}>Download</button>
                    {/* COMPSOD: no Archive on the officer's own record. View stays. */}
                    {!isOwnRecord && (
                      <button style={T.btnGhost} disabled={bgBusy}
                              onClick={() => archiveBg(d.id)}>Archive</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* BGVIEWONLY: NO upload control here, for anyone. The background check is view only on
            this card, because a second upload path for the same document confused users and
            risked two rows for one report. Uploading is done through Fill Compliance, whose
            Section 4 form already carries the report upload, so the message below points there.
            COMPSOD still applies to the Archive button above, which stays hidden on the
            officer's own record. */}
        {/* BGONEMSG: the second sentence here was REMOVED (confirmed decision). The section now
            carries exactly ONE message, the "nothing uploaded yet" line above, and it appears only
            when there is no background check on file. Previously two sentences showed together in
            that state, and the second one also showed BESIDE the table when a check WAS on file,
            which said nothing the table did not already.
            No message in this section mentions Fill Compliance any more. The Fill Compliance
            BUTTON in the New starter onboarding section below is a separate control, untouched.
            COMPSOD IS UNCHANGED: isOwnRecord still suppresses the Archive control above, and the
            server still refuses an upload or archive on the officer's own background check. */}

        {bgArchived.length > 0 && (
          <p style={{ ...T.muted, marginTop: '10px' }}>
            {bgArchived.length} archived {bgArchived.length === 1 ? 'version' : 'versions'}.
          </p>
        )}
      </div>

      {/* NEW STARTER ONBOARDING - the compliance section only. */}
      <div style={T.card}>
        <h3 style={T.title}>New starter onboarding</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ ...T.value, fontWeight: 600, minWidth: '120px' }}>Compliance</span>
          {s4Done ? (
            <>
              <span style={T.pill('#eaf3ee', '#2D4A3E')}>Complete</span>
              <span style={{ fontSize: '12px', color: isDarkMode ? '#8b95a8' : '#888' }}>
                {/* COMPBYNAME: the server resolves the completer to a display name and sends it
                    as section4_completed_by_name. The RAW section4_completed_by is deliberately
                    NOT read here: it stores a bare email on the normal submit path, and rendering
                    it put an address on screen where a name belongs. When the name cannot be
                    resolved the server sends null and a neutral label is shown, never the email. */}
                Completed by {nsForm?.section4_completed_by_name || t('compliance.unknownUser')}
                {(nsForm?.section4a_completed_at || nsForm?.section4_completed_at)
                  ? ` on ${fmtDate(nsForm.section4a_completed_at || nsForm.section4_completed_at)}` : ''}
              </span>
            </>
          ) : (
            <>
              <span style={T.pill('#fdf8ee', '#7a4d00')}>Not submitted</span>
              <button style={T.btn} onClick={() => onFillCompliance(profile || employee)}>Fill Compliance</button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div style={T.card}><p style={T.muted}>Loading...</p></div>
      ) : loadFailed ? (
        <div style={T.card}>
          <LoadError message="Could not load this employee's compliance record." onRetry={load} />
        </div>
      ) : (
        <>
          {/* PENDING COMPLIANCE TASKS */}
          <div style={T.card}>
            <h3 style={T.title}>
              Pending compliance tasks{pendingTasks.length > 0 ? ` (${pendingTasks.length})` : ''}
            </h3>
            {pendingTasks.length === 0 ? (
              <p style={T.muted}>No pending compliance tasks.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={T.th}>Task</th><th style={T.th}>Due</th><th style={T.th}>Status</th>
                </tr></thead>
                <tbody>
                  {pendingTasks.map(t => (
                    <tr key={t.id}>
                      <td style={T.td}>{t.title || t.task_type}</td>
                      <td style={T.td}>{fmtDate(t.due_date)}</td>
                      <td style={T.td}>
                        {t.is_overdue
                          ? <span style={T.pill('#fdf5f5', '#A63A3A')}>Overdue</span>
                          : <span style={T.pill('#fdf8ee', '#7a4d00')}>Pending</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ATTESTATIONS - both states. */}
          <div style={T.card}>
            <h3 style={T.title}>Attestations</h3>

            <p style={{ ...T.label, marginBottom: '6px' }}>
              Completed{attsCompleted.length > 0 ? ` (${attsCompleted.length})` : ''}
            </p>
            {attsCompleted.length === 0 ? (
              <p style={{ ...T.muted, marginBottom: '14px' }}>None completed yet.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                <thead><tr>
                  <th style={T.th}>Type</th><th style={T.th}>Period</th><th style={T.th}>Completed</th>
                </tr></thead>
                <tbody>
                  {attsCompleted.map(a => (
                    <tr key={a.id}>
                      <td style={T.td}>{a.attestation_type}</td>
                      <td style={T.td}>{a.period}</td>
                      <td style={T.td}>{fmtDate(a.attested_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <p style={{ ...T.label, marginBottom: '6px' }}>
              Not yet completed{attsOutstanding.length > 0 ? ` (${attsOutstanding.length})` : ''}
            </p>
            {attsOutstanding.length === 0 ? (
              <p style={T.muted}>None outstanding.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={T.th}>Type</th><th style={T.th}>Period</th><th style={T.th}>Status</th>
                </tr></thead>
                <tbody>
                  {attsOutstanding.map(a => (
                    <tr key={a.id}>
                      <td style={T.td}>{a.attestation_type}</td>
                      <td style={T.td}>{a.period}</td>
                      <td style={T.td}><span style={T.pill('#fdf8ee', '#7a4d00')}>Outstanding</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* DECLARATIONS - the four registers. */}
          {DECLARATION_SECTIONS.map(sec => {
            const rows = detail?.[sec.key] || [];
            return (
              <div key={sec.key} style={T.card}>
                <h3 style={T.title}>{sec.title}{rows.length > 0 ? ` (${rows.length})` : ''}</h3>
                {rows.length === 0 ? (
                  <p style={T.muted}>None submitted.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {sec.cols.map(([, label]) => <th key={label} style={T.th}>{label}</th>)}
                      <th style={T.th}>Status</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(r => (
                        <tr key={r.id}>
                          {sec.cols.map(([field, label, fmt]) => (
                            <td key={label} style={T.td}>
                              {fmt ? fmt(r[field]) : (r[field] ?? '-')}
                            </td>
                          ))}
                          <td style={T.td}>{r.status || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
